/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { breakpoints, useTheme } from '@coursera/cds-core';

import { CARD_IMAGE_DIMENSIONS } from 'bundles/shared-articles/constants';
import type { ImageDimensions } from 'bundles/shared-articles/types';

const placeholderShimmer = keyframes`
  0% {
    background-position: -500px 0;
  }
  100% {
    background-position: 500px 0;
  }
`;

const styles = {
  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  `,
  cardImageContainer: (_: Theme, imageDimensions: ImageDimensions) => css`
    height: ${imageDimensions?.desktopHeight}px;
    width: 100%;

    img {
      width: 100%;
      height: 100%;
    }
    ${breakpoints.down('xs')} {
      height: ${imageDimensions?.mobileHeight}px;
      width: 100%;
    }
  `,
  cardContent: () => css`
    padding: 16px;
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    row-gap: 16px;
    justify-content: space-between;
    ${breakpoints.down('xs')} {
      padding: 10px;
    }
  `,
  cardContentBody: css`
    display: grid;
    row-gap: 8px;
  `,
  titleContainer: css`
    height: 60px;
  `,
  subtitleContainer: () => css`
    height: 60px;
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  publishedDate: css`
    height: 20px;
    width: 30%;
  `,
  estimatedReadingTime: css`
    height: 20px;
    width: 40%;
  `,
  animated: css`
    background-color: #f3f3f3;
    background: linear-gradient(to right, #f3f3f3 5%, var(--cds-color-grey-25) 20%, #f3f3f3 35%);
    animation: ${placeholderShimmer} 2s linear 0s infinite normal forwards;
  `,
};

export type CardProps = {
  showSubtitle?: boolean;
  showImage?: boolean;
  imageDimensions?: ImageDimensions;
};

const ArticleCardPlaceholder = ({
  showSubtitle = true,
  showImage = true,
  imageDimensions = {
    mobileHeight: CARD_IMAGE_DIMENSIONS.mobile.height,
    desktopHeight: CARD_IMAGE_DIMENSIONS.desktop.height,
  },
}: CardProps) => {
  const theme = useTheme();

  return (
    <div css={styles.cardContainer} data-testid="card-placeholder">
      {showImage && <div css={[styles.cardImageContainer(theme, imageDimensions), styles.animated]} />}
      <div css={styles.cardContent} style={!showImage ? { padding: '0px' } : {}}>
        <div css={styles.cardContentBody}>
          <p css={[styles.titleContainer, styles.animated]} />
          {showSubtitle && <p css={[styles.subtitleContainer, styles.animated]} />}
        </div>
        <div>
          <p css={[styles.publishedDate, styles.animated]} />
          <p css={[styles.estimatedReadingTime, styles.animated]} />
        </div>
      </div>
    </div>
  );
};

export default ArticleCardPlaceholder;
